/**
 * The slideshow's use: where it was started, how far it was watched, the settings chosen,
 * and sharing (film or link, which button, encoding time and failures per device type).
 * Also the highlights block's views and clicks, and the memory book's panel.
 * Goes to GTM's dataLayer like the memorial page's other KPI events; no names or texts.
 */

import type { BarButton } from "../highlights/bar";
import type { CardId, TrackedPhase } from "../highlights/cards";
import type { FilmKind } from "./types";

export type DeviceType = "phone" | "tablet" | "desktop";

const TABLET = /iPad|Tablet/i;
const MAC = /Macintosh/;
const PHONE = /Mobi|iPhone|Android/i;
const IOS_VERSION = /OS (\d+)_\d+(?:_\d+)? like Mac OS X/;
const ANDROID_VERSION = /Android (\d+)/;
const WINDOWS = /Windows/;

function deviceType(): DeviceType {
  const ua = navigator.userAgent;
  // iPads report themselves as Macs; touch gives them away.
  if (TABLET.test(ua) || (MAC.test(ua) && navigator.maxTouchPoints > 1)) {
    return "tablet";
  }
  return PHONE.test(ua) ? "phone" : "desktop";
}

function deviceOs(): string {
  const ua = navigator.userAgent;
  const ios = IOS_VERSION.exec(ua);
  if (ios) {
    return `ios-${ios[1]}`;
  }
  const android = ANDROID_VERSION.exec(ua);
  if (android) {
    return `android-${android[1]}`;
  }
  if (WINDOWS.test(ua)) {
    return "windows";
  }
  return MAC.test(ua) ? "macos" : "other";
}

export interface SlideshowEvent {
  action:
    | "play"
    | "add_photos"
    | "settings_open"
    | "share_open"
    | "end_share_shown"
    | "video_ready"
    | "video_failed"
    | "video_interrupted"
    | "video_cancelled"
    | "video_shared"
    | "video_downloaded"
    | "link_shared"
    | "link_copied"
    | "progress"
    | "complete"
    | "setting_changed"
    | "sound_on"
    | "fullscreen";
  audio?: boolean;
  /** Length of the film, seconds. */
  duration?: number;
  /** The life story (the timeline as a film); the slideshow sends none. */
  film?: "life";
  format?: "portrait" | "landscape";
  /** How far into the film, for `progress`: 25, 50 or 75. */
  percent?: number;
  platform?: string;
  reason?: string;
  /** Seconds the encoding took. */
  seconds?: number;
  /** For `setting_changed`: qr, loop, bookends or length. */
  setting?: string;
  /** Where `play` came from: hero, portrait, section, photo, expand, current_events, … */
  source?: string;
  /** Which share button: end_card, share_panel, share_card or share_view. */
  surface?: "end_card" | "share_panel" | "share_card" | "share_view";
  /** What started the film: the slideshow opening on a phone, or a tap on share. */
  trigger?: "open" | "share";
  /** For `setting_changed`: on, off, full or selection. */
  value?: string;
}

let film: FilmKind = "main";

/** The film the viewer plays: its events carry `film: life` for the life story. */
export function trackFilm(kind: FilmKind) {
  film = kind;
}

function push(data: Record<string, unknown>) {
  const w = window as unknown as { dataLayer?: Record<string, unknown>[] };
  if (!Array.isArray(w.dataLayer)) {
    return;
  }
  w.dataLayer.push({ deviceOs: deviceOs(), deviceType: deviceType(), ...data });
}

export function track(event: SlideshowEvent) {
  push({
    event: "memorial_slideshow",
    eventAction: event.action,
    eventCategory: "Memorial Page Slideshow",
    slideshowAudio: event.audio,
    slideshowDuration:
      event.duration === undefined ? undefined : Math.round(event.duration),
    slideshowFilm: event.film ?? (film === "life" ? "life" : undefined),
    slideshowFormat: event.format,
    slideshowPercent: event.percent,
    slideshowPlatform: event.platform,
    slideshowReason: event.reason,
    slideshowSeconds:
      event.seconds === undefined
        ? undefined
        : Math.round(event.seconds * 10) / 10,
    slideshowSetting: event.setting,
    slideshowSource: event.source,
    slideshowSurface: event.surface,
    slideshowTrigger: event.trigger,
    slideshowValue: event.value,
  });
}

export type HighlightEvent =
  | {
      action: "view" | "click";
      card: CardId;
      phase: TrackedPhase;
      position: number;
    }
  /** The mobile bar's middle button, which follows the same phase. */
  | { action: "bar_click"; card: BarButton; phase: TrackedPhase };

/** `card`, `position` and `phase` are event-scoped custom dimensions in GA4. */
export function trackHighlight(event: HighlightEvent) {
  push({
    card: event.card,
    event: "memorial_highlights",
    eventAction: event.action,
    eventCategory: "Memorial Page Highlights",
    phase: event.phase,
    position: "position" in event ? event.position : undefined,
  });
}

export interface BookEvent {
  action:
    | "open"
    | "flip"
    | "size"
    | "coupon_applied"
    | "coupon_refused"
    | "checkout_shown"
    | "checkout_failed"
    | "paid"
    | "app_link"
    | "link_copied"
    | "link_shared"
    | "larger";
  /** For `link_shared`: the service the book's link went out through. */
  platform?: string;
  /** Why the checkout failed or a discount code was refused: the API's error code, or `stripe`. */
  reason?: string;
  size?: "S" | "L";
  /** Where the panel opened: the card's button, its cover, the book's section, or Stripe's return. */
  source?: "button" | "cover" | "return" | "section";
}

/** The memory book's panel on the memorial page, from opening to paying. No names or amounts. */
export function trackBook(event: BookEvent) {
  push({
    bookPlatform: event.platform,
    bookReason: event.reason,
    bookSize: event.size,
    bookSource: event.source,
    event: "memorial_book",
    eventAction: event.action,
    eventCategory: "Memorial Page Memory Book",
  });
}
